import { useAppDispatch, useAppSelector } from "config/store";
import { authActions } from "config/store/modules/auth.ts";
import { tryAsyncFunc } from "globals/functions/async.ts";
import ajax from "config/ajax.ts";
import { Form, Formik } from "formik";
import Button from "components/atom/button/Button.tsx";
import LoadingContainer from "components/container/LoadingContainer.tsx";
import { useTranslation } from "react-i18next";
import ConnectedContainer from "components/container/ConnectedContainer.tsx";
import EventForm from "components/layouts/form/EventForm.tsx";
import { useNavigate } from "react-router-dom";
import { EventFormValuesType } from "utils/form.ts";
import { UserType } from "globals/types/models.ts";
import ConfirmEventPublishModal from "components/modals/ConfirmEventPublishModal.tsx";
import { useCallback, useMemo, useRef, useState } from "react";
import { FormikProps } from "formik/dist/types";
import dayjs from "dayjs";
import { USER_PLAN_TYPES } from "globals/constants.ts";
import { globalActions } from "config/store/modules/global.ts";

function CreateEventPage() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentUser: UserType | null = useAppSelector(state => state.auth.user);
  const [confirmModalCount, setConfirmModalCount] = useState<number>(0);
  const [isOpenConfirmModal, setIsOpenConfirmModal] = useState<boolean>(false);
  //const totalEventCountCreated: number = useAppSelector(state => state.auth.totalEventCountCreated)
  const formRef = useRef<FormikProps<EventFormValuesType> | null>(null);


  const values: EventFormValuesType = {
    active: true,
    titleFr: "",
    titleEn: "",
    descriptionFr: "",
    descriptionEn: "",
    address: "",
    city: "",
    zipcode: "",
    state: "QC",
    country: "CA",
    price: "",
    website: currentUser && currentUser.website ? currentUser.website : "",
    phoneNumber: currentUser && currentUser.phoneNumber ? currentUser.phoneNumber : "",
    externalLink: "",
    categoryId: null,
    latitude: null,
    longitude: null,
    dates: [{
      startDate: dayjs()
        .add(1, "day")
        .add(1, "hour")
        .set("minute", 0)
        .set("second", 0)
        .set("millisecond", 0)
        .format("YYYY-MM-DD HH:mm:ss"),
      endDate: dayjs()
        .add(1, "day")
        .add(2, "hour")
        .set("minute", 0)
        .set("second", 0)
        .set("millisecond", 0)
        .format("YYYY-MM-DD HH:mm:ss")
    }],
    recurring: null,
    images: []
  };

  const canCreateEvent: boolean = useMemo(() => {
    if (!currentUser) return false;

    //cant create event in trial
    if (currentUser.planType === USER_PLAN_TYPES.TRIAL) {
      formRef.current?.setStatus(t("forms.errors.trialLimit"));
      return false;
    }

    if (currentUser.planType === USER_PLAN_TYPES.CANCELED) {
      dispatch(globalActions.setToast({
        type: "error",
        message: t("plans.pleaseSelectBeforeEvent")
      }));
      return false;
    }
    /*if (currentUser.planType === USER_PLAN_TYPES.TRIAL) {
      const trialEnded = currentUser.trialEndedAt ? dayjs(currentUser.trialEndedAt).isBefore(dayjs()) : false;
      if (trialEnded) {
        formRef.current?.setStatus(t("forms.errors.trialLimit"));
        return false;
      }
    } */
    return true;
  }, [currentUser, dispatch, t]);

  const onAcceptConfirmModal = useCallback(() => {
    setIsOpenConfirmModal(false);
    formRef.current?.handleSubmit();
  }, []);

  return (
    <ConnectedContainer title={t("event.new")}>
      <div className="w-full">
        <Formik
          innerRef={formRef}
          initialValues={values}
          validateOnChange={false}
          validateOnBlur={false}
          validate={values => {
            const errors: Partial<{ [K in keyof EventFormValuesType]: string }> = {};//same keys as type but with string value
            if (!values.titleFr) {
              errors.titleFr = t("forms.errors.requiredTitleFr");
            }
            return errors;
          }}
          onSubmit={async (values, { setSubmitting, setStatus, setFieldError }) => {
            if (!canCreateEvent) return;

            //if trial or unit, show confirm modal
            if (currentUser?.planType === USER_PLAN_TYPES.TRIAL || currentUser?.planType === USER_PLAN_TYPES.UNIT) {
              if (confirmModalCount === 0) {
                setIsOpenConfirmModal(true);
                setConfirmModalCount(1);
                return;
              }
            }

            setSubmitting(true);
            setStatus(null);

            const [res, err] = await tryAsyncFunc(ajax.post("/connected/events", values));
            setSubmitting(false);

            if (err) {
              const errData = err.response.data || {};
              const errors = errData.errors || {};

              if (errors.global) {
                setStatus(errors.global);
                delete errors.global;
              }

              Object.keys(errors).forEach(errKey => {
                setFieldError(errKey, errors[errKey]);
              });
              return;
            }

            await dispatch(authActions.createdEvent(res.data.event));
            navigate(`/profile/events/${res.data.event.uuid}`);
          }}
        >
          {({ isSubmitting, status }) => (
            <LoadingContainer isLoading={isSubmitting} keepChildren>
              <Form>
                <EventForm />

                {status && (
                  <p className="error block">{status}</p>
                )}
                <div className="mt-4 flex w-full flex-row items-center justify-center">
                  <Button className="w-full" type="submit" disabled={isSubmitting || !canCreateEvent}>
                    {t("publish")}
                  </Button>
                </div>
              </Form>
            </LoadingContainer>
          )}
        </Formik>
      </div>

      <ConfirmEventPublishModal
        isOpen={isOpenConfirmModal}
        onClose={() => {
          setIsOpenConfirmModal(false);
          navigate(-1);
        }}
        onAccept={onAcceptConfirmModal}
      />
    </ConnectedContainer>
  );
}

export default CreateEventPage;
